import { Content, contentShape, TextArguments } from '../generated/content';

export type Language = 'jp' | 'en';
export type TextKey = keyof TextArguments;
export type Translate = <K extends TextKey>(key: K, ...args: TextArguments[K] extends undefined ? [] : [TextArguments[K]]) => string;

const parameters = (text: string) => Array.from(new Set(Array.from(text.matchAll(/\{([a-zA-Z][\w]*)\}/g), match => match[1]))).sort();

export function validateContent(value: unknown): asserts value is Content {
    function visit(actual: any, expected: any, location: string) {
        if (expected.$text) {
            if ((location === 'content.home.streamUrl' || /^content\.landingPage\.(jp|en)\.contact\.handle$/.test(location)) && actual === '') return;
            if (typeof actual !== 'string' || !actual.trim() || JSON.stringify(parameters(actual)) !== JSON.stringify(expected.$text)) throw new Error(`Invalid content text: ${location}`);
        } else if (expected.$type) {
            if (typeof actual !== expected.$type) throw new Error(`Invalid content value: ${location}`);
        } else if (Array.isArray(expected)) {
            if (!Array.isArray(actual)) throw new Error(`Invalid content array: ${location}`);
            if (expected.length) actual.forEach((item, index) => visit(item, expected[0], `${location}.${index}`));
        } else {
            if (!actual || typeof actual !== 'object' || Array.isArray(actual)) throw new Error(`Invalid content object: ${location}`);
            Object.entries(expected).forEach(([key, child]) => visit(actual[key], child, `${location}.${key}`));
        }
    }
    visit(value, contentShape, 'content');
    const content = value as Content;
    const { destinations, sections, streamUrl } = content.home;
    if ((streamUrl !== '' && !/^https:\/\/(www\.)?twitch\.tv\/[a-zA-Z0-9_]+\/?$/.test(streamUrl))
        || new Set(destinations.map(item => item.id)).size !== destinations.length
        || destinations.some(item => !['gallery', 'article', 'works', 'twitch_guide'].includes(item.pageId))
        || new Set(sections.map(item => item.id)).size !== sections.length
        || sections.some(item => !/^[a-z][a-z0-9_-]*$/.test(item.id) || !['images', 'observations', 'works', 'articles', 'contact'].includes(item.type) || !Number.isInteger(item.limit) || item.limit < 1 || item.limit > 12)) throw new Error('Invalid home references');
    for (const language of ['jp', 'en'] as const) {
        const ids = content.tabs[language].map(tab => tab.id);
        if (new Set(ids).size !== ids.length || ['gallery', 'works', 'article', 'twitch_guide'].some(id => !ids.includes(id))) throw new Error(`Invalid navigation IDs: ${language}`);
    }
}

export function createTranslator(content: Content, language: Language): Translate {
    return ((key: TextKey, args?: Record<string, string | number>) => {
        const template = key.split('.').reduce<any>((value, part) => value?.[part], content.ui[language]);
        if (typeof template !== 'string') throw new Error(`Unknown content key: ${key}`);
        const required = parameters(template);
        if (required.some(name => args?.[name] === undefined) || Object.keys(args || {}).some(name => !required.includes(name))) throw new Error(`Invalid content parameters: ${key}`);
        return template.replace(/\{([a-zA-Z][\w]*)\}/g, (_, name) => String(args![name]));
    }) as Translate;
}
